import React, { Children } from 'react';
import { Layout } from '../components/layout/Layout';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { ministriesData } from '../data/ministriesData';
import { Globe, Heart, Users, Music, Monitor, Sparkles, Apple, Gift, Shield, Baby, Crown } from 'lucide-react';
const getMinistry = (id: string) => ministriesData.find(m => m.id === id);
const categories = [{
  id: 'family-life',
  title: 'FAMILY & LIFE',
  headerClass: 'bg-[#fc0719] text-white',
  ministries: [getMinistry('marriage-ministry'), getMinistry('singles-ministry'), getMinistry('mens-ministry'), getMinistry('womens-ministry'), getMinistry('elderly-ministry')].filter(Boolean)
}, {
  id: 'youth-children',
  title: 'YOUTH & CHILDREN',
  headerClass: 'bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white',
  ministries: [getMinistry('youth-ministry'), getMinistry('childrens-ministry')].filter(Boolean)
}, {
  id: 'worship-creative',
  title: 'WORSHIP & CREATIVE ARTS',
  headerClass: 'bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white',
  ministries: [getMinistry('music-ministry'), getMinistry('tech-media')].filter(Boolean)
}, {
  id: 'service-hospitality',
  title: 'SERVICE & HOSPITALITY',
  headerClass: 'bg-[#0A1F44] text-white',
  ministries: [getMinistry('ushers-greeters'), getMinistry('sanctuary-keepers')].filter(Boolean)
}, {
  id: 'spiritual-growth',
  title: 'PARTNERSHIP',
  headerClass: 'bg-[#0A1F44] text-white',
  ministries: [getMinistry('kingdom-builders')].filter(Boolean)
}, {
  id: 'outreach',
  title: 'OUTREACH & CHARITY',
  headerClass: 'bg-[#fc0719] text-white',
  ministries: [getMinistry('food-distribution'), getMinistry('charity-friends-of-tabitha')].filter(Boolean)
}];
export function MinistriesPage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <Layout data-id="element-1104">
      {/* Hero Section */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-center text-white relative overflow-hidden" data-id="element-1105">
        <div className="absolute inset-0" data-id="element-1106">
          <img src="https://cdn.magicpatterns.com/uploads/4k1paSDENrMMGt5dYW21Re/ChatGPT_Image_Mar_19,_2026,_06_29_56_PM.png" alt="Worship Background" className="w-full h-full object-cover" data-id="element-1107" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/40 via-[#0A1F44]/30 to-[#0A1F44]/60" data-id="element-1108" />

        <div className="container-custom relative z-10 flex flex-col items-center" data-id="element-1109">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-5xl md:text-7xl font-serif font-bold mb-4 tracking-wide text-[#E5D3B3]" style={{
          textShadow: '0 4px 20px rgba(0,0,0,0.5)'
        }} data-id="element-1110">
            OUR MINISTRIES
          </motion.h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-3xl md:text-4xl font-script text-white mb-10" style={{
          textShadow: '0 2px 10px rgba(0,0,0,0.5)'
        }} data-id="element-1111">
            Find your place. Serve With Purpose.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-1112">
            <Link to="/join-team" data-id="element-1113">
              <Button variant="primary" size="lg" className="font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(200,16,46,0.4)]" data-id="element-1114">
                Join a Ministry
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Tagline Section */}
      <div className="bg-[#F5F7FA] py-8 border-b border-gray-200 shadow-sm relative z-20" data-id="element-1115">
        <div className="container-custom text-center" data-id="element-1116">
          <h2 className="text-lg sm:text-xl md:text-2xl font-serif font-bold text-[#8B6508] tracking-wide" data-id="element-1117">
            Raising People{' '}
            <span className="text-[#fc0719] mx-1 sm:mx-2" data-id="element-1118">•</span> Building
            Purpose <span className="text-[#fc0719] mx-1 sm:mx-2" data-id="element-1119">•</span>{' '}
            <span data-id="element-1120">Impacting Lives</span>
          </h2>
        </div>
      </div>

      {/* Categorized Ministry Grid */}
      <section className="py-16 relative overflow-hidden" data-id="element-1121">
        {/* Glittering background */}
        <div className="absolute inset-0" data-id="element-1122">
          <img src="https://cdn.magicpatterns.com/uploads/rnj1ZqYCAXnBEjyB4JUnKZ/ChatGPT_Image_Mar_19,_2026,_01_52_14_PM.png" alt="" className="w-full h-full object-cover" data-id="element-1123" />
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[3px]" data-id="element-1124" />
        </div>

        <div className="container-custom relative z-10 space-y-12" data-id="element-1125">
          {categories.map(category => <div key={category.id} className="space-y-4" data-id="element-1126">
              {/* Category Header Block */}
              <motion.div variants={itemVariants} initial="hidden" whileInView="show" viewport={{
            once: true
          }} className={`w-full ${category.headerClass} p-6 flex items-center justify-center rounded-2xl shadow-lg border border-white/20 relative overflow-hidden group`} data-id="element-1127">
                <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors duration-500" data-id="element-1128" />
                <h2 className="text-2xl md:text-3xl font-serif font-bold tracking-[0.25em] uppercase text-center relative z-10" data-id="element-1129">
                  {category.title}
                </h2>
              </motion.div>

              {/* Ministry Cards Grid for this category */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 auto-rows-min" data-id="element-1130">
                {category.ministries?.map((ministry, idx) => {
              if (!ministry) return null;
              return <motion.div key={ministry.id} variants={itemVariants} initial="hidden" whileInView="show" viewport={{
                once: true
              }} className="col-span-1" data-id="element-1131">
                      <Link to={`/ministries/${ministry.id}`} className="group flex flex-col h-full bg-white/80 backdrop-blur-md rounded-xl shadow-md border border-white/50 overflow-hidden hover:shadow-xl hover:bg-white transition-all duration-300 relative" data-id="element-1132">
                        {/* Category Color Accent Bar */}
                        <div className={`h-1 w-full ${category.id === 'family-life' || category.id === 'outreach' ? 'bg-[#fc0719]' : category.id === 'youth-children' || category.id === 'worship-creative' ? 'bg-[#DAA520]' : 'bg-[#0A1F44]'}`} data-id="element-1133" />

                        {/* Image Container */}
                        <div className="relative overflow-hidden shrink-0 aspect-video" data-id="element-1134">
                          <img src={ministry.image} alt={ministry.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1135" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-1136" />
                        </div>

                        {/* Content */}
                        <div className="p-4 flex flex-col items-center text-center flex-grow" data-id="element-1137">
                          <h3 className="text-lg font-bold text-[#0A1F44] group-hover:text-[#fc0719] transition-colors leading-tight mb-2" data-id="element-1138">
                            {ministry.title}
                          </h3>
                          <div className={`h-[1px] w-12 mb-2 group-hover:w-20 transition-all duration-300 ${category.id === 'family-life' || category.id === 'outreach' ? 'bg-[#fc0719]/30' : category.id === 'youth-children' || category.id === 'worship-creative' ? 'bg-[#DAA520]/30' : 'bg-[#0A1F44]/30'}`} data-id="element-1139" />
                          <p className="text-xs text-gray-600 font-medium tracking-wide uppercase" data-id="element-1140">
                            {ministry.shortDescription.split('.')[0].split(' ').slice(0, 3).join(' · ')}
                          </p>
                        </div>
                      </Link>
                    </motion.div>;
            })}
              </div>
            </div>)}
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-24 relative overflow-hidden text-center" data-id="element-1141">
        {/* Glittering gold background */}
        <div className="absolute inset-0" data-id="element-1142">
          <img src="https://cdn.magicpatterns.com/uploads/pBdCEGj36JKERCJe7TMHjy/ChatGPT_Image_Mar_19,_2026,_01_52_14_PM.png" alt="" className="w-full h-full object-cover" data-id="element-1143" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-black/30" data-id="element-1144" />
        </div>

        <div className="container-custom relative z-10 flex flex-col items-center" data-id="element-1145">
          <div className="flex items-center justify-center gap-4 mb-6 w-full max-w-2xl" data-id="element-1146">
            <div className="h-[1px] flex-grow bg-gradient-to-r from-transparent to-white/50" data-id="element-1147" />
            <motion.h2 initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-5xl md:text-6xl font-script text-[#E5D3B3] px-4" style={{
            textShadow: '0 2px 10px rgba(0,0,0,0.5)'
          }} data-id="element-1148">
              You Belong Here!
            </motion.h2>
            <div className="h-[1px] flex-grow bg-gradient-to-l from-transparent to-white/50" data-id="element-1149" />
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl" data-id="element-1150">
            Join a ministry today and become part of what God is building.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-1151">
            <Link to="/join-team" data-id="element-1152">
              <Button variant="primary" size="lg" className="font-bold tracking-widest uppercase shadow-[0_0_20px_rgba(200,16,46,0.4)]" data-id="element-1153">
                Join a Ministry
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>;
}